// ConvoCX app shell — post-login frame (CON-853, shell polish CON-853 v2).
// Single brand instance (logo + "ConvoCX" wordmark live in the sidebar header
// only), workspace switcher under the logo, theme toggle as an icon button at
// the top-right, and the signed-in identity behind a profile dropdown. Hash
// routing to the FR-12.5 pages. SEO: semantic landmarks + per-route titles.

import { useEffect, useMemo, useState } from 'react';
import {
  AppShell,
  TopNav,
  TopNavHeading,
  SideNav,
  SideNavSection,
  SideNavItem,
  SideNavHeading,
  MobileNav,
  Button,
  TextInput,
  Badge,
  Avatar,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuDivider,
} from '@astryxdesign/core';
import { ChatBubbleLogo, CONVOX_WORDMARK } from '../brand/Logo';
import { useAuth } from '../inbox/auth';
import { api } from '../inbox/api';
import { useHashRoute, routeByName, DEFAULT_ROUTE, type ShellRoute } from './router';
import { usePageMeta } from './usePageMeta';
import { visibleNavSections, isAdminRole } from './navigation';
import { pageFor } from './pages';
import './shell.css';

export interface ShellProps {
  mode: 'dark' | 'light';
  onToggleMode: () => void;
}

const strokeIcon = (children: React.ReactNode, size = 16) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

function SunIcon() {
  return strokeIcon(
    <>
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </>,
  );
}

function MoonIcon() {
  return strokeIcon(<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />);
}

function PlusIcon() {
  return strokeIcon(<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>);
}

function ChevronDownIcon() {
  return strokeIcon(<path d="M6 9l6 6 6-6" />, 14);
}

function CheckIcon() {
  return strokeIcon(<path d="M20 6L9 17l-5-5" />);
}

/** Open-conversation count for the Inbox nav badge (LibreDesk-style queue
 * counts). Best-effort: a failed fetch simply renders no badge. */
function useOpenConversationCount(): number | null {
  const { token } = useAuth();
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (!token) {
      setCount(null);
      return;
    }
    let cancelled = false;
    api
      .listConversations({ status: 'open' })
      .then((res) => {
        if (!cancelled) setCount(res.total);
      })
      .catch(() => {
        if (!cancelled) setCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return count;
}

function SideActions({ onNewConversation }: { onNewConversation: () => void }) {
  return (
    <div className="ccx-shell__side-actions">
      <Button
        label="New conversation"
        variant="primary"
        icon={<PlusIcon />}
        width="100%"
        onClick={onNewConversation}
        data-testid="shell-new-conversation"
      />
    </div>
  );
}

function NavContent({ currentPath, counts }: { currentPath: string; counts?: Record<string, number> }) {
  const { user } = useAuth();
  const sections = useMemo(() => visibleNavSections(user?.role), [user?.role]);

  return (
    <>
      {sections.map((section) => (
        <SideNavSection key={section.title} title={section.title}>
          {section.items.map((item) => {
            const active = currentPath === item.href.replace(/^#/, '');
            const count = counts?.[item.name];
            return (
              <SideNavItem
                key={item.name}
                label={item.label}
                icon={item.icon}
                href={item.href}
                isSelected={active}
                endContent={count != null && count > 0 ? <Badge variant="neutral" label={String(count)} /> : undefined}
                data-testid={`shell-nav-${item.name}`}
              />
            );
          })}
        </SideNavSection>
      ))}
    </>
  );
}

function ThemeToggle({ mode, onToggleMode }: { mode: 'dark' | 'light'; onToggleMode: () => void }) {
  return (
    <Button
      label={mode === 'dark' ? 'Switch to light' : 'Switch to dark'}
      icon={mode === 'dark' ? <SunIcon /> : <MoonIcon />}
      isIconOnly
      variant="ghost"
      onClick={onToggleMode}
      aria-pressed={mode === 'dark'}
      data-testid="theme-toggle"
    />
  );
}

function ProfileMenu() {
  const { user, logout } = useAuth();
  const name = user?.displayName || user?.email || 'Agent';
  const email = user?.email ?? '';

  return (
    <DropdownMenu
      data-testid="shell-profile-menu"
      menuWidth={240}
      button={{
        label: name,
        icon: <Avatar name={name} size="sm" />,
        variant: 'ghost',
      }}
    >
      <DropdownMenuItem label={email} isDisabled />
      <DropdownMenuDivider />
      <DropdownMenuItem label="Sign out" variant="destructive" onClick={logout} />
    </DropdownMenu>
  );
}

function WorkspaceSwitcher() {
  const { user, createWorkspace } = useAuth();
  const wsName = user?.accountName || 'Workspace';
  const [open, setOpen] = useState(false);
  const [prompting, setPrompting] = useState(false);
  const [name, setName] = useState('');

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await createWorkspace(trimmed);
    setOpen(false);
    setPrompting(false);
    setName('');
  };

  return (
    <DropdownMenu
      data-testid="shell-workspace-switcher"
      menuWidth={260}
      isMenuOpen={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setPrompting(false);
      }}
      button={{
        label: wsName,
        size: 'sm',
        variant: 'ghost',
        endContent: <ChevronDownIcon />,
      }}
    >
      <DropdownMenuItem label={wsName} icon={<CheckIcon />} isDisabled />
      <DropdownMenuDivider />
      {prompting ? (
        <div className="ccx-shell__workspace-form" data-testid="shell-workspace-create">
          <TextInput
            label="Workspace name"
            isLabelHidden
            placeholder="e.g. Acme Support"
            value={name}
            onChange={setName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void submit();
              }
            }}
          />
          <Button label="Create workspace" variant="primary" size="sm" onClick={() => void submit()} />
        </div>
      ) : (
        <DropdownMenuItem label="Add new workspace" icon={<PlusIcon />} hasCloseOnSelect={false} onClick={() => setPrompting(true)} />
      )}
    </DropdownMenu>
  );
}

export function Shell({ mode, onToggleMode }: ShellProps) {
  const { user } = useAuth();
  const { path, navigate } = useHashRoute();
  const openConversations = useOpenConversationCount();
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    if (openConversations != null) c.inbox = openConversations;
    return c;
  }, [openConversations]);

  const routeName = path.replace(/^\//, '');
  let route: ShellRoute = routeByName(routeName);
  // Route-level role gating (least privilege): admin-only pages fall back to
  // the inbox for non-admin roles, matching the nav menu gating.
  const adminOnly = ['teams', 'channels', 'templates', 'canned', 'labels', 'knowledge', 'csat', 'settings', 'billing'].includes(route.name);
  const isAdmin = isAdminRole(user?.role);
  if (adminOnly && !isAdmin) {
    route = routeByName(DEFAULT_ROUTE);
  }
  usePageMeta(route.title, route.description);

  // A role-gated nav item may be hidden for the current role; fall back to the
  // inbox route when the hash points at a hidden/unknown page.
  useEffect(() => {
    if (route.name === routeName) return;
    navigate(`#/${route.name}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeName, route.name]);

  const Page = pageFor(route.name);

  return (
    <AppShell
      height="fill"
      contentPadding={0}
      topNav={
        <TopNav
          label="Top navigation"
          heading={<TopNavHeading heading={route.title} />}
          endContent={
            <>
              <ThemeToggle mode={mode} onToggleMode={onToggleMode} />
              <ProfileMenu />
            </>
          }
        />
      }
      sideNav={
        <SideNav
          header={
            <div className="ccx-shell__side-head">
              <SideNavHeading
                icon={<ChatBubbleLogo size={20} />}
                heading="ConvoCX"
                headingHref="#/inbox"
              />
              <WorkspaceSwitcher />
            </div>
          }
          topContent={<SideActions onNewConversation={() => navigate('#/inbox')} />}
          data-testid="shell-sidenav"
        >
          <NavContent currentPath={path} counts={counts} />
        </SideNav>
      }
      mobileNav={{
        breakpoint: 'md',
        content: (
          <MobileNav
            label="Navigation"
            header={
              <div className="ccx-shell__drawer-head">
                <ChatBubbleLogo size={22} />
                {CONVOX_WORDMARK}
                <span style={{ marginLeft: 'auto' }}>
                  <ThemeToggle mode={mode} onToggleMode={onToggleMode} />
                </span>
              </div>
            }
            width={320}
            data-testid="shell-mobile-nav"
          >
            <SideActions onNewConversation={() => navigate('#/inbox')} />
            <NavContent currentPath={path} counts={counts} />
          </MobileNav>
        ),
      }}
    >
      <Page mode={mode} onToggleMode={onToggleMode} />
    </AppShell>
  );
}